import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { Box, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { TransformerModalModelResponse } from 'src/Libs/shop/models'
import { addPowerMeter } from 'src/State/ShopSystemConfiguration/ShopSystemConfigurationAction'
import { ShopMainSkeleton } from 'src/Libs/shop/components/Skeletons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import useShopSystemConfiguration from '../../hooks/use-shop-system-configuration'
import TransformerCard from './Transformer/TransformerCard'
import TransformerMoreInfoProduct from './Transformer/TransformerMoreInfoProduct'
import { TransformerType } from '../../types'

export interface TransformerModalProps {
  transformerType: TransformerType
  id: string
  powerMeterId: string
  onSingleProductAdd?: (selectedTransformerId: string) => void | Promise<void>
  open: boolean
  place: number
  onClose: () => void
}

const useStyles = makeStyles()(theme => ({
  cardWrapper: {
    marginTop: theme.spacing(2),
    display: 'grid',
    alignItems: 'strech',
    justifyContent: 'center',
    justifyItems: 'center',
    gap: theme.spacing(4),
    gridAutoRows: '1fr',
    gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(auto-fit, 270px)',
      justifyContent: 'center',
    },
  },
}))

/*
 * This Component Shows Transformers List for an current Power Meter that is beeing added to the Basket from the System Configurator
 */
const TransformerModal: React.FC<TransformerModalProps> = ({
  open,
  place,
  onClose,
  onSingleProductAdd,
  id,
  powerMeterId,
  transformerType,
}): JSX.Element => {
  const [productData, setProductData] = useState<TransformerModalModelResponse | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const { getCurrentTransformers } = useShopSystemConfiguration()
  const dispatch = useDispatch()
  const { t } = useCustomTranslation()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [moreInfoProduct, setMoreInfoProduct] = useState<string | null>(null)
  const desiredProduct = productData?.elements.find(product => product.id === moreInfoProduct) ?? null
  const { classes } = useStyles()

  useEffect(() => {
    getCurrentTransformers()
      .then(result => setProductData(result))
      .finally(() => setIsLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleConfirm = async (): Promise<void> => {
    if (transformerType === TransformerType.SystemConfigure) {
      dispatch(
        addPowerMeter({
          id,
          powerMeterId,
          powerConverterId: selectedId,
        })
      )
      onClose()
      return
    }

    if (transformerType === TransformerType.SingleProduct && onSingleProductAdd && selectedId) {
      await onSingleProductAdd(selectedId)
      onClose()
    }
  }

  const handleSelectedProduct = (productId: string | null): void => {
    setSelectedId(productId)
  }

  const handleMoreInfoProduct = (productId: string | null): void => {
    setMoreInfoProduct(productId)
  }

  const modalActions = {
    primary: {
      name: t('shop.common.further'),
      disabled: selectedId === null,
      onClick: () => {
        handleConfirm()
      },
    },
  }

  return (
    <Dialog
      title={moreInfoProduct !== null ? undefined : t('shop.transformer.add-power-meter')}
      maxWidth={moreInfoProduct !== null ? 'sm' : 'md'}
      onClose={onClose}
      place={place}
      actions={modalActions}
    >
      <Box>
        {moreInfoProduct === null && (
          <Typography sx={{ my: 1 }} variant="h3" textAlign="left">
            {t('shop.transformer.title')}
          </Typography>
        )}
        {isLoading ? (
          <Box>
            <ShopMainSkeleton shopSkeletonType="product" />
          </Box>
        ) : (
          <Box>
            {moreInfoProduct === null ? (
              <Box className={classes.cardWrapper}>
                {productData?.elements
                  .sort((a, b) => a.name?.localeCompare(b.name))
                  .map(product => {
                    return (
                      <TransformerCard
                        key={product.id}
                        productData={product}
                        selectedProductId={selectedId}
                        showPrice
                        onSelectedPrductIdChange={handleSelectedProduct}
                        onMoreInfoProduct={handleMoreInfoProduct}
                      />
                    )
                  })}
              </Box>
            ) : (
              <Box>
                {desiredProduct && <TransformerMoreInfoProduct productData={desiredProduct} onMoreInfoProductChange={handleMoreInfoProduct} />}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Dialog>
  )
}

export default TransformerModal
